import { RFP, RFPStatus, RFPSide } from '../types';

// ── RFP win-rate analytics ─────────────────────────────────────────────
// Win rate = Won / (Won + Lost + No Award). Only decided RFPs count toward
// the denominator; in-flight ones are excluded. Grouped by BD lead (owner),
// client, source (responding vs issuing), and time period (year-quarter).

export interface WinRateRow {
  dimension: 'BD Lead' | 'Client' | 'Source' | 'Period';
  group: string;
  won: number;
  lost: number;
  noAward: number;
  decided: number;
  winRatePct: number | null; // null when no decided RFPs
}

const isWon = (r: RFP) => r.dagrun_pm_status === RFPStatus.Won;
const isLost = (r: RFP) => r.dagrun_pm_status === RFPStatus.Lost;
const isNoAward = (r: RFP) => r.dagrun_pm_status === RFPStatus.NoAward;
const isDecided = (r: RFP) => isWon(r) || isLost(r) || isNoAward(r);

function periodKey(r: RFP): string {
  const iso = r.createdon;
  if (!iso) return 'Unknown';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Unknown';
  return `${d.getUTCFullYear()} Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
}
function sourceKey(r: RFP): string {
  if (r.dagrun_pm_side === RFPSide.Responding) return 'Responding to RFP';
  if (r.dagrun_pm_side === RFPSide.Issuing) return 'Issuing RFP';
  return 'Unspecified';
}

function group(rfps: RFP[], dimension: WinRateRow['dimension'], keyer: (r: RFP) => string): WinRateRow[] {
  const map = new Map<string, { won: number; lost: number; noAward: number }>();
  for (const r of rfps) {
    if (!isDecided(r)) continue;
    const k = keyer(r) || '—';
    const cur = map.get(k) ?? { won: 0, lost: 0, noAward: 0 };
    if (isWon(r)) cur.won++; else if (isLost(r)) cur.lost++; else cur.noAward++;
    map.set(k, cur);
  }
  return Array.from(map.entries())
    .map(([g, c]) => {
      const decided = c.won + c.lost + c.noAward;
      return {
        dimension, group: g, won: c.won, lost: c.lost, noAward: c.noAward, decided,
        winRatePct: decided > 0 ? Math.round((c.won / decided) * 1000) / 10 : null,
      };
    })
    .sort((a, b) => (b.decided - a.decided) || a.group.localeCompare(b.group));
}

export interface WinRateAnalytics {
  overall: WinRateRow;
  byBDLead: WinRateRow[];
  byClient: WinRateRow[];
  bySource: WinRateRow[];
  byPeriod: WinRateRow[];
}

export function computeWinRate(rfps: RFP[]): WinRateAnalytics {
  const decided = rfps.filter(isDecided);
  const won = decided.filter(isWon).length;
  const lost = decided.filter(isLost).length;
  const noAward = decided.filter(isNoAward).length;
  const overall: WinRateRow = {
    dimension: 'BD Lead', group: 'Overall', won, lost, noAward,
    decided: decided.length,
    winRatePct: decided.length > 0 ? Math.round((won / decided.length) * 1000) / 10 : null,
  };
  return {
    overall,
    byBDLead: group(rfps, 'BD Lead', r => r.dagrun_pm_ownername ?? 'Unassigned'),
    byClient: group(rfps, 'Client', r => r.dagrun_pm_clientname ?? 'Unknown client'),
    bySource: group(rfps, 'Source', sourceKey),
    byPeriod: group(rfps, 'Period', periodKey),
  };
}

function csvCell(v: string | number | null): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Build a CSV covering all four dimensions plus an overall summary row. */
export function winRateCsv(rfps: RFP[]): string {
  const a = computeWinRate(rfps);
  const header = ['Dimension', 'Group', 'Won', 'Lost', 'No Award', 'Decided', 'Win Rate %'];
  const rows: WinRateRow[] = [
    { ...a.overall, dimension: 'BD Lead', group: 'OVERALL' },
    ...a.byBDLead, ...a.byClient, ...a.bySource, ...a.byPeriod,
  ];
  const lines = [header.join(',')];
  for (const r of rows) {
    lines.push([r.dimension, r.group, r.won, r.lost, r.noAward, r.decided, r.winRatePct == null ? '' : r.winRatePct]
      .map(csvCell).join(','));
  }
  return lines.join('\n');
}
